/**
 * useWasteMapLayers - Hook para inicializar capas reales de Waste en el mapa
 *
 * @remarks
 * Carga datos REALES de contenedores, depósitos y rutas desde la API
 * y los visualiza en el mapa usando MapLibre layers.
 *
 * FASE B: Corrección de visibilidad - Solo inicialización de capas, NO UI de búsqueda.
 */

import { useEffect, useRef } from 'react';
import type { GeoJSONSource, MapGeoJSONFeature, MapLayerMouseEvent } from 'maplibre-gl';
import { mapEngine } from '../../../map-engine/MapEngineService';
import { wasteApi } from '../services';

// Source and layer IDs
const CONTAINERS_SOURCE = 'waste-containers-source';
const CONTAINERS_LAYER = 'waste-containers';
const DEPOTS_SOURCE = 'waste-depots-source';
const DEPOTS_LAYER = 'waste-depots';
const HOTSPOTS_SOURCE = 'waste-hotspots-source';
const HOTSPOTS_LAYER = 'waste-hotspots';
const ROUTES_SOURCE = 'waste-routes-source';
const ROUTES_LAYER = 'waste-routes';

// Colors following WESTEER palette
const COLORS = {
  containers: {
    active: '#3b82f6', // blue-500
    full: '#ef4444', // red-500
    maintenance: '#f59e0b', // amber-500
  },
  depots: '#8b5cf6', // purple-500
  hotspots: '#f97316', // orange-500
  routes: '#06b6d4', // cyan-500
};

export interface UseWasteMapLayersOptions {
  projectId: string;
  visible: boolean;
}

function getWasteFeatureId(feature: MapGeoJSONFeature): string | null {
  if (typeof feature.id === 'string' || typeof feature.id === 'number') {
    return String(feature.id);
  }

  const properties = feature.properties as { id?: string | number; containerId?: string | number } | undefined;
  const candidate = properties?.id ?? properties?.containerId;
  return candidate !== undefined && candidate !== null ? String(candidate) : null;
}

function getPointCoordinates(feature: MapGeoJSONFeature): [number, number] | null {
  if (feature.geometry.type !== 'Point') {
    return null;
  }

  const [lng, lat] = feature.geometry.coordinates;
  return typeof lng === 'number' && typeof lat === 'number' ? [lng, lat] : null;
}

/**
 * Hook que inicializa y gestiona capas reales de Waste en el mapa
 */
export function useWasteMapLayers({ projectId, visible }: UseWasteMapLayersOptions): void {
  const layersCreatedRef = useRef(false);
  const delayedReloadRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !visible || !projectId) return;

    const initializeLayers = (): void => {
      if (layersCreatedRef.current) return;
      layersCreatedRef.current = true;

      console.log('[useWasteMapLayers] Inicializando capas de Waste...');

      // 1. CONTAINERS
      if (!map.getSource(CONTAINERS_SOURCE)) {
        map.addSource(CONTAINERS_SOURCE, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
      }

      if (!map.getLayer(CONTAINERS_LAYER)) {
        map.addLayer({
          id: CONTAINERS_LAYER,
          type: 'circle',
          source: CONTAINERS_SOURCE,
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10,
              3.5,
              15,
              6,
              18,
              8.5,
            ],
            'circle-color': [
              'case',
              ['==', ['get', 'status'], 'full'],
              COLORS.containers.full,
              ['==', ['get', 'status'], 'maintenance'],
              COLORS.containers.maintenance,
              COLORS.containers.active,
            ],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.9,
          },
        });
        console.log('[useWasteMapLayers] ✓ Capa containers creada');
      }

      // 2. DEPOTS
      if (!map.getSource(DEPOTS_SOURCE)) {
        map.addSource(DEPOTS_SOURCE, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
      }

      if (!map.getLayer(DEPOTS_LAYER)) {
        map.addLayer({
          id: DEPOTS_LAYER,
          type: 'circle',
          source: DEPOTS_SOURCE,
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10,
              6,
              15,
              12,
              18,
              16,
            ],
            'circle-color': COLORS.depots,
            'circle-stroke-width': 3,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.9,
          },
        });
        console.log('[useWasteMapLayers] ✓ Capa depots creada');
      }

      // 3. HOTSPOTS
      if (!map.getSource(HOTSPOTS_SOURCE)) {
        map.addSource(HOTSPOTS_SOURCE, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
      }

      if (!map.getLayer(HOTSPOTS_LAYER)) {
        map.addLayer({
          id: HOTSPOTS_LAYER,
          type: 'circle',
          source: HOTSPOTS_SOURCE,
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10,
              5,
              15,
              9,
              18,
              12,
            ],
            'circle-color': COLORS.hotspots,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.92,
          },
        });
        console.log('[useWasteMapLayers] ✓ Capa hotspots creada');
      }

      // 4. ROUTES
      if (!map.getSource(ROUTES_SOURCE)) {
        map.addSource(ROUTES_SOURCE, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
      }

      if (!map.getLayer(ROUTES_LAYER)) {
        map.addLayer({
          id: ROUTES_LAYER,
          type: 'line',
          source: ROUTES_SOURCE,
          paint: {
            'line-color': COLORS.routes,
            'line-width': 3,
            'line-opacity': 0.8,
          },
        });
        console.log('[useWasteMapLayers] ✓ Capa routes creada');
      }
    };

    const loadData = async (): Promise<void> => {
      try {
        console.log(`[useWasteMapLayers] Cargando datos de proyecto: ${projectId}`);

        // Cargar GeoJSON para cada tipo de entidad
        const [containersGeoJSON, depotsGeoJSON, hotspotsGeoJSON, routesGeoJSON] = await Promise.all([
          wasteApi.containers.getGeoJSON(projectId),
          wasteApi.depots.getGeoJSON(projectId),
          wasteApi.hotspots.getGeoJSON(projectId),
          wasteApi.routes.getGeoJSON(projectId),
        ]);

        // Actualizar sources con datos reales
        const containersSource = map.getSource(CONTAINERS_SOURCE) as GeoJSONSource;
        if (containersSource) {
          containersSource.setData(containersGeoJSON);
          console.log(
            `[useWasteMapLayers] ✓ Contenedores cargados: ${containersGeoJSON.features.length} features`
          );
        }

        const depotsSource = map.getSource(DEPOTS_SOURCE) as GeoJSONSource;
        if (depotsSource) {
          depotsSource.setData(depotsGeoJSON);
          console.log(
            `[useWasteMapLayers] ✓ Depósitos cargados: ${depotsGeoJSON.features.length} features`
          );
        }

        const hotspotsSource = map.getSource(HOTSPOTS_SOURCE) as GeoJSONSource;
        if (hotspotsSource) {
          hotspotsSource.setData(hotspotsGeoJSON);
          console.log(
            `[useWasteMapLayers] ✓ Microbasurales cargados: ${hotspotsGeoJSON.features.length} features`
          );
        }

        const routesSource = map.getSource(ROUTES_SOURCE) as GeoJSONSource;
        if (routesSource) {
          routesSource.setData(routesGeoJSON);
          console.log(
            `[useWasteMapLayers] ✓ Rutas cargadas: ${routesGeoJSON.features.length} features`
          );
        }
      } catch (error) {
        console.error('[useWasteMapLayers] Error cargando datos:', error);
      }
    };

    // Inicializar capas y cargar datos
    initializeLayers();
    loadData();

    const handleContainerClick = (event: MapLayerMouseEvent): void => {
      const feature = event.features?.[0];
      if (!feature) return;

      const containerId = getWasteFeatureId(feature);
      if (!containerId) {
        console.warn('[useWasteMapLayers] Click en contenedor sin id resoluble');
        return;
      }

      const coordinates = getPointCoordinates(feature) ?? undefined;

      window.dispatchEvent(
        new CustomEvent('waste-container-select', {
          detail: {
            containerId,
            coordinates,
            source: 'map',
          },
        }),
      );
    };

    const handleContainerMouseEnter = (): void => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const handleContainerMouseLeave = (): void => {
      if (map.getCanvas().style.cursor === 'pointer') {
        map.getCanvas().style.cursor = '';
      }
    };

    const handleDepotClick = (event: MapLayerMouseEvent): void => {
      const feature = event.features?.[0];
      if (!feature) return;

      const depotId = getWasteFeatureId(feature);
      if (!depotId) {
        console.warn('[useWasteMapLayers] Click en depósito sin id resoluble');
        return;
      }

      const coordinates = getPointCoordinates(feature) ?? undefined;

      window.dispatchEvent(
        new CustomEvent('waste-depot-select', {
          detail: {
            depotId,
            coordinates,
            source: 'map',
          },
        }),
      );
    };

    const handleHotspotClick = (event: MapLayerMouseEvent): void => {
      const feature = event.features?.[0];
      if (!feature) return;

      const hotspotId = getWasteFeatureId(feature);
      if (!hotspotId) {
        console.warn('[useWasteMapLayers] Click en microbasural sin id resoluble');
        return;
      }

      const coordinates = getPointCoordinates(feature) ?? undefined;

      window.dispatchEvent(
        new CustomEvent('waste-hotspot-select', {
          detail: {
            hotspotId,
            coordinates,
            source: 'map',
          },
        }),
      );
    };

    const handlePointMouseEnter = (): void => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const handlePointMouseLeave = (): void => {
      if (map.getCanvas().style.cursor === 'pointer') {
        map.getCanvas().style.cursor = '';
      }
    };

    map.on('click', CONTAINERS_LAYER, handleContainerClick);
    map.on('mouseenter', CONTAINERS_LAYER, handleContainerMouseEnter);
    map.on('mouseleave', CONTAINERS_LAYER, handleContainerMouseLeave);
    map.on('click', DEPOTS_LAYER, handleDepotClick);
    map.on('mouseenter', DEPOTS_LAYER, handlePointMouseEnter);
    map.on('mouseleave', DEPOTS_LAYER, handlePointMouseLeave);
    map.on('click', HOTSPOTS_LAYER, handleHotspotClick);
    map.on('mouseenter', HOTSPOTS_LAYER, handlePointMouseEnter);
    map.on('mouseleave', HOTSPOTS_LAYER, handlePointMouseLeave);

    const handleDataChange = (): void => {
      void loadData();

      if (delayedReloadRef.current) {
        clearTimeout(delayedReloadRef.current);
      }

      // Segunda recarga corta para absorber carreras justo después del commit/import.
      delayedReloadRef.current = setTimeout(() => {
        void loadData();
        delayedReloadRef.current = null;
      }, 750);
    };

    window.addEventListener('container-created', handleDataChange);
    window.addEventListener('container-updated', handleDataChange);
    window.addEventListener('container-deleted', handleDataChange);
    window.addEventListener('depot-created', handleDataChange);
    window.addEventListener('depot-updated', handleDataChange);
    window.addEventListener('depot-deleted', handleDataChange);
    window.addEventListener('hotspot-created', handleDataChange);
    window.addEventListener('hotspot-updated', handleDataChange);
    window.addEventListener('hotspot-deleted', handleDataChange);

    // Cleanup al desmontar
    return () => {
      window.removeEventListener('container-created', handleDataChange);
      window.removeEventListener('container-updated', handleDataChange);
      window.removeEventListener('container-deleted', handleDataChange);
      window.removeEventListener('depot-created', handleDataChange);
      window.removeEventListener('depot-updated', handleDataChange);
      window.removeEventListener('depot-deleted', handleDataChange);
      window.removeEventListener('hotspot-created', handleDataChange);
      window.removeEventListener('hotspot-updated', handleDataChange);
      window.removeEventListener('hotspot-deleted', handleDataChange);

      if (delayedReloadRef.current) {
        clearTimeout(delayedReloadRef.current);
        delayedReloadRef.current = null;
      }

      if (!map) return;

      map.off('click', CONTAINERS_LAYER, handleContainerClick);
      map.off('mouseenter', CONTAINERS_LAYER, handleContainerMouseEnter);
      map.off('mouseleave', CONTAINERS_LAYER, handleContainerMouseLeave);
      map.off('click', DEPOTS_LAYER, handleDepotClick);
      map.off('mouseenter', DEPOTS_LAYER, handlePointMouseEnter);
      map.off('mouseleave', DEPOTS_LAYER, handlePointMouseLeave);
      map.off('click', HOTSPOTS_LAYER, handleHotspotClick);
      map.off('mouseenter', HOTSPOTS_LAYER, handlePointMouseEnter);
      map.off('mouseleave', HOTSPOTS_LAYER, handlePointMouseLeave);
      if (map.getCanvas().style.cursor === 'pointer') {
        map.getCanvas().style.cursor = '';
      }

      console.log('[useWasteMapLayers] Limpiando capas...');

      // Remover layers
      if (map.getLayer(CONTAINERS_LAYER)) map.removeLayer(CONTAINERS_LAYER);
      if (map.getLayer(DEPOTS_LAYER)) map.removeLayer(DEPOTS_LAYER);
      if (map.getLayer(HOTSPOTS_LAYER)) map.removeLayer(HOTSPOTS_LAYER);
      if (map.getLayer(ROUTES_LAYER)) map.removeLayer(ROUTES_LAYER);

      // Remover sources
      if (map.getSource(CONTAINERS_SOURCE)) map.removeSource(CONTAINERS_SOURCE);
      if (map.getSource(DEPOTS_SOURCE)) map.removeSource(DEPOTS_SOURCE);
      if (map.getSource(HOTSPOTS_SOURCE)) map.removeSource(HOTSPOTS_SOURCE);
      if (map.getSource(ROUTES_SOURCE)) map.removeSource(ROUTES_SOURCE);

      layersCreatedRef.current = false;
    };
  }, [projectId, visible]);
}
